import React, { useState, useEffect } from "react";

function LimitsEditor(props) {
  const [selectedOption, setSelectedOption] = useState("INCLUSION");

  useEffect(() => {
    return () => {
      Inventum.labels.clearLimitBounds();
    };
  }, []);

  const updateSelected = (option) => {
    setSelectedOption(option);
  };

  const addLimit = () => {
    Inventum.labels.addLimit(props.data.id, selectedOption);
    props.data.getLabels();
  };

  const handleDelete = (limitID) => {
    Inventum.labels.deleteRenderLimit(props.data.id, limitID);
    props.data.getLabels();
  };

  const handleValue = (limitID) => {
    const labelID = props.data.id;
    const limit = props.data.renderLimits[limitID];

    if (limit.type === "EXCLUSION" || limit.type === "INCLUSION") {
      // For inclusion and exclusion, we don't provide an optional value.
      // Instead we ask the camera manager for the current position
      const position = Inventum.camera.getCameraPositionWorld(); 
      // Merge with the existing { x, y, z, d } object
      const updatedValue = Object.assign({}, limit.value, position);
      Inventum.labels.updateRenderLimit(labelID, limitID, updatedValue);
    } else {
      // For everything else, prompt the user for a numeric value
      let providedValue = prompt("Value:", limit.value);

      if (providedValue == null || providedValue === "") return;
      providedValue = Number(providedValue);
      if (isNaN(providedValue)) {
        return;
      }
      Inventum.labels.updateRenderLimit(labelID, limitID, providedValue);
    }
    props.data.getLabels();
  };

  const handleOther = (limitID) => {
    const labelID = props.data.id;
    const limit = props.data.renderLimits[limitID];
    if (limit.type !== "EXCLUSION" && limit.type !== "INCLUSION") {
      return;
    }

    const currentValue = limit.value;
    let newDistance = prompt("Distance:", currentValue.d);
    if (newDistance == null || newDistance === "") return;
    newDistance = Number(newDistance);
    if (isNaN(newDistance)) {
      return;
    }

    currentValue.d = newDistance;
    Inventum.labels.updateRenderLimit(labelID, limitID, currentValue);
    props.data.getLabels();
  };

  // Split the array into inc/exc vs other
  const incExcLimits = [];
  const otherLimits = [];
  props.data.renderLimits.forEach((limit, index) => {
    limit.limitID = index;
    if (limit.type === "INCLUSION" || limit.type === "EXCLUSION") {
      incExcLimits.push(limit);
    } else {
      otherLimits.push(limit);
    }
  });

  // If 2D label is scene-stateable, show disabled message
  if (props.data.isSceneStateable) {
    return (
      <div className="LimitsDisabledMessage">
        <span className="material-icons" style={{ fontSize: "48px", opacity: 0.5, marginBottom: "10px" }}>block</span>
        <h3 style={{ margin: "0 0 10px 0", fontSize: "14px" }}>Limits Disabled</h3>
        <p>Limits only work on Screen labels that are not "SceneState-able".</p>
        <p>To use limits, disable the scene state option in the header (green box icon).</p>
      </div>
    );
  }

  return (
    <div className="LimitsEditorContainer">
      <InclusionExclusionLimitTable
        limits={incExcLimits}
        handleDelete={handleDelete}
        handleValue={handleValue}
        handleOther={handleOther}
      />
      <OtherLimitTable
        limits={otherLimits}
        handleValue={handleValue}
        handleDelete={handleDelete}
      />
      <div className="LimitsAddSection">
        <div className="LabelSelectDropdownContainer LimitsTypeSelectWrap">
          <select
            className="LabelTextDropdown LabelSelectDropdown"
            value={selectedOption}
            onChange={(e) => {
              updateSelected(e.target.value);
            }}
          >
            <option value="INCLUSION">INCLUSION</option>
            <option value="EXCLUSION">EXCLUSION</option>
            <option value="ABOVE_ALTITUDE">ABOVE_ALTITUDE</option>
            <option value="BELOW_ALTITUDE">BELOW_ALTITUDE</option>
            <option value="DISTANCE">DISTANCE</option>
          </select>
          <i className="material-icons notranslate LabelTextDropdownArrow LabelSelectDropdownArrow">
            arrow_drop_down
          </i>
        </div>
        <button className="LimitsAddButton" onClick={addLimit}>
          Add limit
        </button>
      </div>
    </div>
  );
}

function InclusionExclusionLimitTable(props) {
  if (props.limits.length === 0) return null;
  
  return (
    <div className="LimitCardContainer">
      <div className="LimitTableTitle">Inclusion/Exclusion Limits</div>
      <div className="LimitCardList">
        {props.limits.map((limit, index) => (
          <InclusionLimitRow
            handleValue={props.handleValue}
            handleOther={props.handleOther}
            handleDelete={props.handleDelete}
            key={index}
            {...limit}
          />
        ))}
      </div>
    </div>
  );
}

function OtherLimitTable(props) {
  if (props.limits.length === 0) return null;
  
  return (
    <div className="LimitCardContainer">
      <div className="LimitTableTitle">Other Limits</div>
      <div className="LimitCardList">
        {props.limits.map((limit, index) => (
          <OtherLimitRow
            handleValue={props.handleValue}
            handleDelete={props.handleDelete}
            key={index}
            {...limit}
          />
        ))}
      </div>
    </div>
  );
}

function InclusionLimitRow(props) {
  let value = props.value;
  let otherValue = "N/A";

  if (props.type === "INCLUSION" || props.type === "EXCLUSION") {
    value = "Set Position";
    otherValue = props.value.d;
  }

  const createDebug = () => {
    Inventum.labels.showLimitBounds(props.value);
  };

  return (
    <div className="LimitCard">
      <div className="LimitCardHeader">
        <div>
          <span className="LimitCardEyebrow">Limit</span>
          <span className="LimitCardType">{props.type}</span>
        </div>
        <button
          className="LimitActionButton LimitActionButton--red LimitActionButton--compact"
          onClick={() => {
            props.handleDelete(props.limitID);
          }}
        >
          <span className="material-icons">delete</span>
          Delete
        </button>
      </div>
      <div className="LimitCardGrid LimitCardGrid--inclusion">
        <div className="LimitCardField LimitCardField--wide">
          <span className="LimitCardFieldLabel">Position</span>
          <button
            className="LimitActionButton LimitActionButton--teal"
            title={JSON.stringify(props.value)}
            onClick={() => {
              props.handleValue(props.limitID);
            }}
          >
            <span className="material-icons">my_location</span>
            {value}
          </button>
        </div>
        <div className="LimitCardField">
          <span className="LimitCardFieldLabel">Radius</span>
          <button
            className="LimitActionButton LimitActionButton--teal"
            onClick={() => {
              props.handleOther(props.limitID);
            }}
          >
            {otherValue}
          </button>
        </div>
        <div className="LimitCardField">
          <span className="LimitCardFieldLabel">Debug</span>
          <button className="LimitActionButton LimitActionButton--blue" onClick={createDebug}>
            <span className="material-icons">visibility</span>
            Show
          </button>
        </div>
      </div>
    </div>
  );
}

function OtherLimitRow(props) {
  let value = props.value;
  if (props.type === "INCLUSION" || props.type === "EXCLUSION") {
    value = "Set Position";
  }

  return (
    <div className="LimitCard">
      <div className="LimitCardHeader">
        <div>
          <span className="LimitCardEyebrow">Limit</span>
          <span className="LimitCardType">{props.type}</span>
        </div>
        <button
          className="LimitActionButton LimitActionButton--red LimitActionButton--compact"
          onClick={() => {
            props.handleDelete(props.limitID);
          }}
        >
          <span className="material-icons">delete</span>
          Delete
        </button>
      </div>
      <div className="LimitCardGrid">
        <div className="LimitCardField LimitCardField--wide">
          <span className="LimitCardFieldLabel">Value</span>
          <button
            className="LimitActionButton LimitActionButton--teal"
            title={JSON.stringify(props.value)}
            onClick={() => {
              props.handleValue(props.limitID);
            }}
          >
            <span className="material-icons">edit</span>
            {value}
          </button>
        </div>
      </div>
    </div>
  );
}

function LimitsPanel(props) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <div
        className="EditorDrawerItemHeader"
        onClick={() => setOpen(!open)}
      >
        <span className="PanelOpenStatus">{open ? "▾" : "▸"}</span>
        <span>Limits</span>
      </div>
      {open ? (
        <LimitsEditor
          data={{ ...props, closeLimitsEditor: () => setOpen(false) }}
        />
      ) : null}
    </div>
  );
}

export { LimitsPanel };
